const observer = new IntersectionObserver(
  entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add("is-visible");
        observer.unobserve(entry.target);
      }
    });
  },
  { threshold: 0.15 }
);

document.querySelectorAll(".animate").forEach(el => observer.observe(el));

// Header scroll behavior. Direction-aware visibility past 100px:
//   - scrolling DOWN  → show .hero-cta-sticky only. Hero header stays in-flow
//                       (off-screen above the fold) — no .scrolled class so the
//                       slide-in animation never runs.
//   - scrolling UP    → reparent hero-header to <body>, add .scrolled to slide
//                       it in as a fixed bar. Hide .hero-cta-sticky.
//   - at top (≤100px) → reset to in-flow header, no sticky CTA.
const heroHeader = document.querySelector('.hero-header');
const heroCard = document.querySelector('.hero-card');
const stickyCta = document.querySelector('.hero-cta-sticky');

// Move sticky CTA to <body> so its fixed positioning + z-index escape every
// ancestor's stacking context, containing block (transform/filter/contain),
// and overflow clipping.
if (stickyCta && stickyCta.parentElement !== document.body) {
  document.body.appendChild(stickyCta);
}

if (heroHeader && heroCard) {
  let originalParent = heroHeader.parentElement;
  let originalNextSibling = heroHeader.nextElementSibling;
  let lastScrollY = window.scrollY;
  let isReparented = false;

  const reparentToBody = () => {
    if (!isReparented) {
      originalParent = heroHeader.parentElement;
      originalNextSibling = heroHeader.nextElementSibling;
      document.body.appendChild(heroHeader);
      isReparented = true;
    }
  };

  const reparentBack = () => {
    if (isReparented && originalParent) {
      if (originalNextSibling && originalNextSibling.parentElement === originalParent) {
        originalParent.insertBefore(heroHeader, originalNextSibling);
      } else {
        originalParent.appendChild(heroHeader);
      }
      isReparented = false;
    }
  };

  const handleScroll = () => {
    const currentScrollY = window.scrollY;
    // Micro-jitter threshold: ignore deltas < 2px so the state doesn't flicker
    const delta = currentScrollY - lastScrollY;
    const direction = Math.abs(delta) < 2 ? null : (delta > 0 ? 'down' : 'up');

    if (currentScrollY <= 100) {
      // Back at top — reset.
      heroHeader.classList.remove('scrolled');
      heroCard.classList.remove('header-scrolled');
      if (stickyCta) stickyCta.classList.remove('is-visible');
      reparentBack();
    } else if (direction === 'down') {
      // Scrolling down past 100 — only the sticky CTA pill.
      // Hero header goes back in-flow inside .hero-card (off-screen above fold).
      heroHeader.classList.remove('scrolled');
      heroCard.classList.remove('header-scrolled');
      reparentBack();
      if (stickyCta) stickyCta.classList.add('is-visible');
    } else if (direction === 'up') {
      // Scrolling up past 100 — show the scrolled header bar, hide the CTA.
      reparentToBody();
      heroHeader.classList.add('scrolled');
      heroCard.classList.add('header-scrolled');
      if (stickyCta) stickyCta.classList.remove('is-visible');
    }
    // direction === null: micro-jitter, leave state untouched

    lastScrollY = currentScrollY;
  };

  window.addEventListener('scroll', handleScroll, { passive: true });

  // Initial state: if loaded already past 100 (reload mid-scroll, deep link, etc.)
  // default to the "scroll-down" state so the CTA is visible immediately.
  if (window.scrollY > 100) {
    if (stickyCta) stickyCta.classList.add('is-visible');
  } else {
    handleScroll();
  }
}

// Scroll-driven parallax for science section product layers
const parallaxWrapper = document.querySelector('.science-parallax-wrapper');
const parallaxGummies = document.querySelector('.science-parallax-gummies');

if (parallaxWrapper && parallaxGummies) {
  const updateParallax = () => {
    const rect = parallaxWrapper.getBoundingClientRect();
    const centerOffset = rect.top + rect.height / 2 - window.innerHeight / 2;
    parallaxGummies.style.transform = `translateY(${centerOffset * 0.18}px)`;
  };

  window.addEventListener('scroll', updateParallax, { passive: true });
  updateParallax();
}

// Buy CTA → add to cart, then open the cart drawer.
// Drives the homepage purchase widget (sections/home-purchase.liquid) and the
// in-article product widget (sections/main-article.liquid). The PDP buy box keeps
// its own handler because it also does bundle/label/disabled sync.
//
// Posts to /cart/add.js (the JSON AJAX endpoint) rather than routes.cart_add_url,
// which resolves to the legacy HTML form endpoint — same reasoning as
// sections/pdp-buy-box.liquid and sections/cart-drawer.liquid.
(function () {
  let isAddingToCart = false;

  document.addEventListener('click', async e => {
    const trigger = e.target.closest('[data-buy-checkout]');
    if (!trigger || isAddingToCart) return;
    e.preventDefault();

    // Resolve the purchase type from the radios in this CTA's own form. Scoping to
    // the form matters: the article widget has no radios, and a document-wide
    // lookup would make it follow the homepage's selection.
    const form = trigger.closest('form');
    const selected = form && form.querySelector('input[name="purchase-type"]:checked');
    const isSubscription = selected
      ? selected.value === 'subscription'
      : Boolean(trigger.dataset.subscriptionVariantId);

    const variantId = trigger.dataset.variantId
      || (isSubscription ? trigger.dataset.subscriptionVariantId : trigger.dataset.onetimeVariantId);
    if (!variantId) return;

    const payload = { id: parseInt(variantId, 10), quantity: 1 };

    // Without the selling plan id the subscription option would add a plain
    // one-time line item. Absent id means no selling plan group on the product —
    // fall through as one-time rather than failing the add.
    const sellingPlanId = isSubscription ? trigger.dataset.subscriptionSellingPlanId : null;
    if (sellingPlanId) payload.selling_plan = parseInt(sellingPlanId, 10);

    const originalText = trigger.textContent;
    isAddingToCart = true;
    trigger.disabled = true;
    trigger.textContent = trigger.dataset.loadingText || 'Adding...';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart.');

      // Open the cart drawer instead of jumping to checkout. cart:updated MUST carry
      // detail.item_count (the badge fan-out in layout/theme.liquid reads it) and MUST
      // come BEFORE cart:open — the drawer's cart:updated listener no-ops while closed,
      // so this costs one /cart.js; the reverse order fetches twice.
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();

      // Undo the loading state BEFORE opening the drawer, for two reasons: the page
      // no longer navigates away, so leaving isAddingToCart true would dead the CTA
      // for good; and a disabled element drops focus to <body>, which is what
      // openDrawer() would then capture and restore to on close.
      isAddingToCart = false;
      trigger.disabled = false;
      trigger.textContent = originalText;
      // Re-enabling does not restore focus that disabling dropped to <body>, and the
      // drawer captures document.activeElement to return to on close.
      trigger.focus();

      window.dispatchEvent(new CustomEvent('cart:updated', { detail: { item_count: cart.item_count } }));
      window.dispatchEvent(new Event('cart:open'));
    } catch (err) {
      console.error('[buy-checkout] add-to-cart failed:', err);
      isAddingToCart = false;
      trigger.disabled = false;
      trigger.textContent = originalText;
      alert(err.message || 'Could not add to cart. Please try again.');
    }
  });
})();

// Precise anchor scroll: offset by the fixed nav height at the moment of click
document.querySelectorAll('a.smooth-scroll[href^="#"]').forEach(link => {
  link.addEventListener('click', e => {
    const targetId = link.getAttribute('href');
    const target = document.querySelector(targetId);
    if (!target) return;
    e.preventDefault();

    // Only count the nav height if it's currently fixed (scrolled state)
    const nav = document.querySelector('.hero-header');
    const navOffset = (nav && nav.classList.contains('scrolled')) ? nav.offsetHeight : 0;

    const targetTop = target.getBoundingClientRect().top + window.scrollY - navOffset;
    window.scrollTo({ top: targetTop, behavior: 'smooth' });
  });
});

// Long-form prose tables: the editors behind both `.rte` surfaces (blog articles and
// the policy pages) emit a bare <table> with no wrapper, and a 4-column comparison
// table can't shrink below a readable width. Give each one its own horizontal
// scroller so a wide table scrolls instead of forcing the page to.
// The `.rte > div > table` arm is for Shopify's policy markup, which nests the whole
// body in one bare <div>. Styling lives in assets/component-rte.css (.rte-table-wrap).
document.querySelectorAll('.rte > table, .rte > div > table').forEach(table => {
  const wrap = document.createElement('div');
  wrap.className = 'rte-table-wrap';
  table.parentNode.insertBefore(wrap, table);
  wrap.appendChild(table);
});

// Policy pages (/policies/*): Shopify's stock policy copy has no block structure to
// style. The Terms of Service is three <p> elements carrying a hundred-odd <br>s —
// every paragraph break and every section heading in it is a line break — and the
// Shipping Policy runs its headings into the copy as `<p><strong>Name</strong> body…`.
// component-rte.css builds the article's vertical rhythm out of *sibling* margins, so
// against that markup it has nothing to act on and the page renders as one slab.
// CSS cannot fix it: display/height on a <br> is ignored by the engine.
//
// So rebuild the blocks the copy implies — split each paragraph at its breaks, and
// promote a run that is (or opens with) a lone <strong> to an <h3>. From there the
// shared rules do the rest, and a policy lays out exactly like a blog post.
//
// This is damage control for merchant copy this theme does not own. Re-authoring the
// policies in Settings → Policies with real <p>/<h2> is the actual fix; this code then
// finds nothing to change and stops mattering. Without JS the copy still reads, just
// densely — same trade-off as the table wrapper above.
(function normalisePolicyProse() {
  const root = document.querySelector('.policy-page .rte');
  if (!root) return;

  const isBlank = (node) => node.nodeType === Node.TEXT_NODE && !node.textContent.trim();

  // A heading, not a sentence that happens to start bold: short, and not punctuated
  // like prose. Guards the run-in case, where the rest of the line is body copy.
  const readsAsHeading = (text) => text.length > 0 && text.length <= 80 && !/[.!?,;:]$/.test(text);

  root.querySelectorAll('p').forEach((p) => {
    // Split on <br>, dropping the breaks themselves and any all-whitespace run.
    const runs = [[]];
    p.childNodes.forEach((node) => {
      if (node.nodeName === 'BR') runs.push([]);
      else runs[runs.length - 1].push(node);
    });
    const groups = runs
      .map((run) => {
        while (run.length && isBlank(run[0])) run.shift();
        while (run.length && isBlank(run[run.length - 1])) run.pop();
        return run;
      })
      .filter((run) => run.length);
    if (!groups.length) return;

    const blocks = [];
    groups.forEach((group) => {
      const lead = group[0];
      if (lead.nodeName === 'STRONG' && readsAsHeading(lead.textContent.trim())) {
        const h = document.createElement('h3');
        h.textContent = lead.textContent.trim();
        blocks.push(h);
        group.shift();
        while (group.length && isBlank(group[0])) group.shift();
        if (!group.length) return;
      }
      const para = document.createElement('p');
      group.forEach((node) => para.appendChild(node));
      blocks.push(para);
    });

    // One plain paragraph in, one plain paragraph out — leave well-authored copy alone.
    if (blocks.length === 1 && blocks[0].nodeName === 'P') return;
    p.replaceWith(...blocks);
  });
})();
